import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  MapPin,
  Clock,
  Sparkles,
  ArrowRight,
  Plus,
  Route,
  Compass } from
'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTripPlanner } from '../contexts/TripPlannerContext';
import { useLanguage } from '../contexts/LanguageContext';
export function TripPlannerPromo() {
  const { user } = useAuth();
  const { plans } = useTripPlanner();
  const { lang, isRTL } = useLanguage();
  const isAr = lang === 'ar';
  const hasPlans = !!user && plans.length > 0;
  const planCount = plans.length;
  const lastPlan = hasPlans ?
  [...plans].sort(
    (a, b) =>
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )[0] :
  null;
  // Benefits list (shown for logged-out users)
  const benefits = [
  {
    icon: Calendar,
    en: 'Plan day-by-day',
    ar: 'خطّط يوم بيوم'
  },
  {
    icon: Clock,
    en: 'Auto-timed stops',
    ar: 'توقيتات تلقائية'
  },
  {
    icon: Route,
    en: 'See route on a map',
    ar: 'شوف المسار على الخريطة'
  },
  {
    icon: Compass,
    en: 'Sort by your location',
    ar: 'رتّب حسب موقعك'
  }];

  return (
    <section>
      <motion.div
        initial={{
          opacity: 0,
          y: 20
        }}
        whileInView={{
          opacity: 1,
          y: 0
        }}
        viewport={{
          once: true,
          margin: '-100px'
        }}
        transition={{
          duration: 0.6
        }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sea-blue via-sea-blue to-sea-blue-light text-white shadow-float">
        
        {/* Decorative map/route SVG — pure design */}
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.18] pointer-events-none"
          viewBox="0 0 800 400"
          fill="none"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true">
          
          {/* Grid */}
          <defs>
            <pattern
              id="trip-grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse">
              
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="white"
                strokeWidth="0.5" />
              
            </pattern>
          </defs>
          <rect width="800" height="400" fill="url(#trip-grid)" />
          {/* Dashed route */}
          <path
            d="M 60 320 Q 180 200 320 240 T 560 140 T 740 100"
            stroke="#E8A87C"
            strokeWidth="3"
            strokeDasharray="8 8"
            fill="none" />
          
          {/* Pins */}
          {[
          [60, 320],
          [220, 230],
          [400, 220],
          [560, 140],
          [740, 100]].
          map(([x, y], i) =>
          <g key={i} transform={`translate(${x}, ${y})`}>
              <circle r="14" fill="#C75C3F" />
              <circle r="14" fill="none" stroke="white" strokeWidth="2.5" />
              <text
              y="5"
              textAnchor="middle"
              fontSize="14"
              fontWeight="700"
              fill="white"
              fontFamily="sans-serif">
              
                {i + 1}
              </text>
            </g>
          )}
        </svg>

        {/* Glow accents */}
        <div className="absolute -top-20 -end-20 w-72 h-72 bg-terracotta/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -start-20 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-6 p-8 sm:p-10 lg:p-14">
          {/* Left: Text + CTA (3 cols) */}
          <div className="lg:col-span-3 flex flex-col justify-center">
            {/* Top eyebrow */}
            <div className="flex items-center gap-2 mb-4">
              <span
                className={`inline-flex items-center gap-1.5 bg-terracotta text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${isAr ? 'font-arabic tracking-normal' : ''}`}>
                
                <Sparkles size={12} />
                {isAr ? 'جديد' : 'New'}
              </span>
              <span
                className={`text-xs font-bold uppercase tracking-wider text-white/70 ${isAr ? 'font-arabic tracking-normal' : ''}`}>
                
                {isAr ? 'مخطط الرحلات' : 'Trip Planner'}
              </span>
            </div>

            {!user &&
            <>
                <h2
                className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight ${isAr ? 'font-arabic' : ''}`}>
                
                  {isAr ?
                'خطط رحلتك المثالية في إسكندرية' :
                'Plan your perfect Alexandria trip'}
                </h2>
                <p
                className={`text-white/85 text-base sm:text-lg mb-6 max-w-xl leading-relaxed ${isAr ? 'font-arabic' : ''}`}>
                
                  {isAr ?
                'اعمل خطة يوم بيوم، ضيف الأماكن اللي عاوزها، واحنا نحسبلك التوقيتات والمسافات. الخريطة، التوقيت، والمسار — كله في مكان واحد.' :
                'Build a day-by-day plan, add the places you love, and we’ll calculate the timings and distances for you. Your map, schedule, and route — all in one place.'}
                </p>

                {/* Benefits grid */}
                <div className="grid grid-cols-2 gap-2.5 max-w-lg mb-7">
                  {benefits.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div
                      key={b.en}
                      className={`flex items-center gap-2 text-sm text-white/90 ${isAr ? 'font-arabic' : ''}`}>
                      
                        <span className="w-7 h-7 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
                          <Icon size={14} />
                        </span>
                        <span>{isAr ? b.ar : b.en}</span>
                      </div>);

                })}
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                  to="/auth/signup?redirect=/account/plans"
                  className={`inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-6 py-3 rounded-xl font-semibold transition-colors shadow-lg ${isAr ? 'font-arabic' : ''}`}>
                  
                    <Sparkles size={16} />
                    {isAr ? 'سجّل وابدأ مجاناً' : 'Sign up — start free'}
                  </Link>
                  <Link
                  to="/auth/signin?redirect=/account/plans"
                  className={`inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 text-white px-6 py-3 rounded-xl font-medium transition-colors ${isAr ? 'font-arabic' : ''}`}>
                  
                    {isAr ? 'تسجيل الدخول' : 'Sign in'}
                    <ArrowRight
                    size={14}
                    className={isRTL ? 'rotate-180' : ''} />
                  
                  </Link>
                </div>
                <p
                className={`text-xs text-white/55 mt-3 ${isAr ? 'font-arabic' : ''}`}>
                
                  {isAr ?
                'مجاناً تماماً — مفيش بطاقة ائتمان.' :
                'Completely free — no credit card required.'}
                </p>
              </>
            }

            {user && !hasPlans &&
            <>
                <h2
                className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight ${isAr ? 'font-arabic' : ''}`}>
                
                  {isAr ?
                `أهلاً ${user.displayName?.split(' ')[0] || ''} — جاهز نخطط رحلتك؟` :
                `Hi ${user.displayName?.split(' ')[0] || 'there'} — ready to plan a trip?`}
                </h2>
                <p
                className={`text-white/85 text-base sm:text-lg mb-7 max-w-xl leading-relaxed ${isAr ? 'font-arabic' : ''}`}>
                
                  {isAr ?
                'لسه ما عملتش أي خطة. اعمل خطة جديدة، ضيف الأماكن اللي شايفها، واحنا نرتّبهالك على اليوم.' :
                "You haven't created any plans yet. Start one, add the places you have in mind, and we'll arrange your day for you."}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                  to="/account/plans"
                  className={`inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-6 py-3 rounded-xl font-semibold transition-colors shadow-lg ${isAr ? 'font-arabic' : ''}`}>
                  
                    <Plus size={16} />
                    {isAr ? 'ابدأ خطتك الأولى' : 'Start your first plan'}
                  </Link>
                </div>
              </>
            }

            {user && hasPlans &&
            <>
                <h2
                className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight ${isAr ? 'font-arabic' : ''}`}>
                
                  {isAr ? 'كمّل تخطيط رحلتك' : 'Continue planning your trip'}
                </h2>
                <p
                className={`text-white/85 text-base sm:text-lg mb-6 max-w-xl leading-relaxed ${isAr ? 'font-arabic' : ''}`}>
                
                  {isAr ?
                `عندك ${planCount} ${planCount === 1 ? 'خطة محفوظة' : 'خطط محفوظة'}. ارجع لخطتك آخر مرة أو اعمل واحدة جديدة.` :
                `You have ${planCount} saved ${planCount === 1 ? 'plan' : 'plans'}. Jump back into your latest one or start a new trip.`}
                </p>
                {lastPlan &&
              <Link
                to={`/account/plans/${lastPlan.id}`}
                className="block mb-5 bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl p-4 transition-colors max-w-md group">
                
                    <div
                  className={`text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1 ${isAr ? 'font-arabic tracking-normal' : ''}`}>
                  
                      {isAr ? 'آخر خطة عدّلتها' : 'Last edited'}
                    </div>
                    <div
                  className={`font-serif text-xl font-bold text-white group-hover:text-sand transition-colors mb-1 ${isAr ? 'font-arabic' : ''}`}>
                  
                      {lastPlan.name}
                    </div>
                    <div
                  className={`flex items-center gap-3 text-xs text-white/70 ltr-numerals ${isAr ? 'font-arabic' : ''}`}>
                  
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={11} />
                        {lastPlan.days.length}{' '}
                        {lastPlan.days.length === 1 ?
                    isAr ?
                    'يوم' :
                    'day' :
                    isAr ?
                    'أيام' :
                    'days'}
                      </span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} />
                        {lastPlan.days.reduce(
                      (s, d) => s + d.stops.length,
                      0
                    )}{' '}
                        {isAr ? 'مكان' : 'stops'}
                      </span>
                    </div>
                  </Link>
              }
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                  to="/account/plans"
                  className={`inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-6 py-3 rounded-xl font-semibold transition-colors shadow-lg ${isAr ? 'font-arabic' : ''}`}>
                  
                    {isAr ? 'كل خططي' : 'All my plans'}
                    <ArrowRight
                    size={14}
                    className={isRTL ? 'rotate-180' : ''} />
                  
                  </Link>
                  <Link
                  to="/account/plans"
                  className={`inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 text-white px-6 py-3 rounded-xl font-medium transition-colors ${isAr ? 'font-arabic' : ''}`}>
                  
                    <Plus size={14} />
                    {isAr ? 'خطة جديدة' : 'New plan'}
                  </Link>
                </div>
              </>
            }
          </div>

          {/* Right: Decorative mock plan card (2 cols, hidden on mobile) */}
          <div className="hidden lg:flex lg:col-span-2 items-center justify-center">
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95,
                rotate: -2
              }}
              whileInView={{
                opacity: 1,
                scale: 1,
                rotate: -3
              }}
              viewport={{
                once: true
              }}
              transition={{
                duration: 0.7,
                delay: 0.2
              }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-5 w-full max-w-xs text-sea-blue">
              
              <div className="flex items-center justify-between mb-3">
                <div
                  className={`text-[10px] font-bold uppercase tracking-wider text-terracotta ${isAr ? 'font-arabic tracking-normal' : ''}`}>
                  
                  {isAr ? 'خطة عيّنة' : 'Sample plan'}
                </div>
                <div className="flex items-center gap-1 text-xs font-bold text-sea-blue/80 ltr-numerals">
                  <Calendar size={11} />
                  Day 1
                </div>
              </div>
              <div
                className={`font-serif text-lg font-bold mb-4 leading-tight ${isAr ? 'font-arabic' : ''}`}>
                
                {isAr ? 'يوم على الكورنيش' : 'A day on the Corniche'}
              </div>
              <div className="space-y-2.5">
                {[
                {
                  n: 1,
                  t: '09:00',
                  en: 'Sea Breeze Café',
                  ar: 'مقهى نسيم البحر'
                },
                {
                  n: 2,
                  t: '11:30',
                  en: 'Qaitbay Citadel',
                  ar: 'قلعة قايتباي'
                },
                {
                  n: 3,
                  t: '14:00',
                  en: 'Anfoushi Fish Market',
                  ar: 'سوق الأنفوشي'
                },
                {
                  n: 4,
                  t: '17:30',
                  en: 'Stanley Bridge',
                  ar: 'كوبري ستانلي'
                }].
                map((stop) =>
                <div
                  key={stop.n}
                  className="flex items-center gap-2.5 text-xs">
                  
                    <div className="w-6 h-6 rounded-full bg-terracotta text-white flex items-center justify-center font-bold text-[11px] shrink-0 ltr-numerals">
                      {stop.n}
                    </div>
                    <div className="flex-grow min-w-0">
                      <div
                      className={`font-serif font-bold truncate ${isAr ? 'font-arabic' : ''}`}>
                      
                        {isAr ? stop.ar : stop.en}
                      </div>
                    </div>
                    <div className="text-[10px] font-semibold text-sea-blue/55 shrink-0 ltr-numerals">
                      {stop.t}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-sea-blue/10 flex items-center justify-between text-[10px] text-sea-blue/80">
                <span className={isAr ? 'font-arabic' : ''}>
                  {isAr ? '4 محطات' : '4 stops'}
                </span>
                <span className="ltr-numerals">8h 30m</span>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>);

}